import {
  RICH_HTML_BODY_FONT_SIZE,
  RICH_HTML_BODY_LINE_HEIGHT,
  RICH_HTML_PLACEHOLDER_COLOR,
  richHtmlScopedCss,
} from './richHtmlVisualContract';

/**
 * Wide tables/images only scroll when the scrollport is width-bounded.
 * `overflow-x: auto` on an element that grows with its content is a no-op;
 * flex items also need `minWidth: 0` so they can shrink below content size.
 */

export const RICH_TEXT_WEB_SCROLLPORT_STYLE = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  overflowX: 'auto',
  overflowY: 'auto',
} as const;

export const RICH_TEXT_WIDE_TABLE_CSS = `
table {
  border-collapse: collapse;
  margin: 8px 0;
  width: max-content;
  max-width: none;
  table-layout: auto;
}
td, th {
  border: none;
  min-width: 80px;
  padding: 6px 8px;
  vertical-align: top;
  word-break: normal;
  overflow-wrap: normal;
}
table[data-ss-grid] td,
table[data-ss-grid] th {
  border: 1px solid #D1D5DB;
}
th {
  background: #F3F4F6;
  font-weight: 700;
}
`;

/** 10tap's own scrollport: `#root > div` (position absolute, height 100%). */
export const RICH_TEXT_NATIVE_SCROLLPORT_SELECTOR =
  '#root > div:nth-of-type(1)';

/** Stop looking for the scrollport after this many 100ms retries (~5s). */
export const RICH_TEXT_NATIVE_SCROLL_INDICATOR_ATTACH_ATTEMPTS = 50;

/** Room kept under the caret so the line being typed is never clipped. */
export const RICH_TEXT_NATIVE_CARET_BOTTOM_GAP = 24;

/**
 * Native 10tap WebView sits in the request form's vertical
 * KeyboardAwareScrollView. Bound the editor width and let wide tables
 * overflow-x so testers can pan (1106 issue 3).
 *
 * Both axes scroll on 10tap's scrollport, never on `.ProseMirror`: any
 * `overflow` there computes the other axis from `visible` to `auto`, which
 * makes the editable element its own scroll container. It then absorbs
 * WebKit's caret-into-view scroll (it has nothing to scroll, being auto
 * height), so typing past the bottom of the field leaves the caret clipped.
 */
export const RICH_TEXT_NATIVE_SCROLLPORT_CSS = `
html, body { width: 100%; max-width: 100%; margin: 0; }
${RICH_TEXT_NATIVE_SCROLLPORT_SELECTOR} {
  overflow-x: auto;
  overflow-y: auto;
}
.ProseMirror {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  /*
   * Scrollable room under the last line. !important because 10tap resets the
   * inline paddingBottom to 0 on Android ~200ms after mount; revisit if this
   * editor ever turns on avoidIosKeyboard, which writes the keyboard height
   * into that same inline style.
   */
  padding-bottom: ${RICH_TEXT_NATIVE_CARET_BOTTOM_GAP}px !important;
}
${RICH_TEXT_WIDE_TABLE_CSS}
`;

/**
 * iOS and Android WebViews only draw overlay scroll indicators for the main
 * frame, so a bounded editor gives testers no sign that content continues
 * below the fold. Paint our own (see `RICH_TEXT_NATIVE_SCROLL_INDICATOR_JS`).
 */
export const RICH_TEXT_NATIVE_SCROLL_INDICATOR_CSS = `
#ss-scroll-indicator {
  position: fixed;
  top: 2px;
  right: 2px;
  bottom: 2px;
  width: 3px;
  display: none;
  pointer-events: none;
  z-index: 5;
}
#ss-scroll-indicator-thumb {
  width: 3px;
  border-radius: 2px;
  background-color: rgba(17, 24, 39, 0.32);
}
`;

export const RICH_TEXT_NATIVE_CONTENT_WIDTH_MESSAGE = 'ss-content-width';

/** Cap so a bad WebView payload cannot size the host to Infinity. */
export const RICH_TEXT_NATIVE_MAX_CONTENT_WIDTH = 8192;

/**
 * 10tap `configureCSS` *replaces* `extendCSS`. Keep the PlaceholderBridge
 * `::before` rule when we also inject the wide-content scrollport CSS.
 */
export const RICH_TEXT_NATIVE_PLACEHOLDER_CSS = `
.is-editor-empty:first-child::before {
  color: ${RICH_HTML_PLACEHOLDER_COLOR};
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
  font-size: ${RICH_HTML_BODY_FONT_SIZE}px;
  line-height: ${RICH_HTML_BODY_LINE_HEIGHT}px;
}
.ProseMirror:has(table) .is-editor-empty::before {
  content: none !important;
}
`;

export const RICH_TEXT_NATIVE_EDITOR_CSS = `
${RICH_TEXT_NATIVE_PLACEHOLDER_CSS}
${RICH_TEXT_NATIVE_SCROLLPORT_CSS}
${RICH_TEXT_NATIVE_SCROLL_INDICATOR_CSS}
${richHtmlScopedCss('.ProseMirror')}
`;

/**
 * Sizes and positions the indicator thumb from the scrollport metrics, then
 * keeps it in sync with scrolling and content edits. Re-injected on every
 * WebView load because 10tap remounts the WebView once on iOS.
 */
export const RICH_TEXT_NATIVE_SCROLL_INDICATOR_JS = `
(function () {
  var MIN_THUMB = 24;
  var generation = (window.__ssRichTextScrollIndicatorGeneration || 0) + 1;
  window.__ssRichTextScrollIndicatorGeneration = generation;
  function scrollport() {
    var exact = document.querySelector('${RICH_TEXT_NATIVE_SCROLLPORT_SELECTOR}');
    if (exact) {
      return exact;
    }
    var root = document.getElementById('root');
    var node = document.querySelector('.ProseMirror');
    while (node && node.parentElement && node.parentElement !== root) {
      node = node.parentElement;
    }
    return node && node.parentElement === root ? node : null;
  }
  function paint() {
    var host = scrollport();
    if (!host || !document.body) {
      return;
    }
    var bar = document.getElementById('ss-scroll-indicator');
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'ss-scroll-indicator';
      var newThumb = document.createElement('div');
      newThumb.id = 'ss-scroll-indicator-thumb';
      bar.appendChild(newThumb);
      document.body.appendChild(bar);
    }
    var thumb = document.getElementById('ss-scroll-indicator-thumb');
    var view = host.clientHeight;
    var content = host.scrollHeight;
    if (!view || !thumb || content <= view + 1) {
      bar.style.display = 'none';
      return;
    }
    bar.style.display = 'block';
    var track = Math.max(0, view - 4);
    var size = Math.max(MIN_THUMB, Math.round((track * view) / content));
    var scrollable = content - view;
    var offset = scrollable > 0
      ? Math.round((host.scrollTop / scrollable) * (track - size))
      : 0;
    thumb.style.height = size + 'px';
    thumb.style.transform = 'translateY(' + offset + 'px)';
  }
  var frame = 0;
  function schedulePaint() {
    if (frame) {
      return;
    }
    var requestFrame = window.requestAnimationFrame || function (callback) {
      return setTimeout(callback, 16);
    };
    frame = requestFrame(function () {
      frame = 0;
      paint();
    });
  }
  function unbind(binding) {
    binding.host.removeEventListener('scroll', binding.onScroll);
    window.removeEventListener('resize', binding.onResize);
    if (binding.observer) {
      binding.observer.disconnect();
    }
  }
  var remaining = ${RICH_TEXT_NATIVE_SCROLL_INDICATOR_ATTACH_ATTEMPTS};
  function attach() {
    if (window.__ssRichTextScrollIndicatorGeneration !== generation) {
      return;
    }
    var host = scrollport();
    if (!host) {
      if (remaining > 0) {
        remaining -= 1;
        setTimeout(attach, 100);
      }
      return;
    }
    host.style.overflowX = 'auto';
    host.style.overflowY = 'auto';
    var prior = window.__ssRichTextScrollIndicatorBinding;
    if (prior && prior.host === host) {
      schedulePaint();
      return;
    }
    if (prior) {
      unbind(prior);
    }
    var observer = null;
    if (typeof MutationObserver !== 'undefined') {
      observer = new MutationObserver(schedulePaint);
      observer.observe(host, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
    host.addEventListener('scroll', schedulePaint, { passive: true });
    window.addEventListener('resize', schedulePaint);
    window.__ssRichTextScrollIndicatorBinding = {
      host: host,
      observer: observer,
      onScroll: schedulePaint,
      onResize: schedulePaint
    };
    schedulePaint();
  }
  attach();
})();
true;
`;

/** Posted back to RN so the wrapping horizontal ScrollView can size the WebView. */
export const RICH_TEXT_NATIVE_MEASURE_WIDTH_JS = `
(function() {
  function report() {
    var pm = document.querySelector('.ProseMirror');
    var tableW = 0;
    var tables = document.querySelectorAll('table');
    for (var i = 0; i < tables.length; i++) {
      tableW = Math.max(tableW, tables[i].scrollWidth);
    }
    var imgW = 0;
    var imgs = document.querySelectorAll('img');
    for (var j = 0; j < imgs.length; j++) {
      imgW = Math.max(imgW, imgs[j].scrollWidth || 0);
    }
    var w = Math.ceil(Math.max(
      document.documentElement.scrollWidth || 0,
      document.body ? document.body.scrollWidth : 0,
      pm ? pm.scrollWidth : 0,
      tableW,
      imgW
    ));
    if (!isFinite(w) || w <= 0) {
      return;
    }
    if (w > ${RICH_TEXT_NATIVE_MAX_CONTENT_WIDTH}) {
      w = ${RICH_TEXT_NATIVE_MAX_CONTENT_WIDTH};
    }
    if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: '${RICH_TEXT_NATIVE_CONTENT_WIDTH_MESSAGE}',
        payload: w
      }));
    }
  }
  report();
  if (!window.__ssRichTextWidthObs && typeof MutationObserver !== 'undefined') {
    var timer = null;
    window.__ssRichTextWidthObs = new MutationObserver(function() {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(report, 80);
    });
    var root = document.body || document.documentElement;
    if (root) {
      window.__ssRichTextWidthObs.observe(root, {
        childList: true,
        subtree: true,
        attributes: true
      });
    }
  }
})();
true;
`;

export function sanitizeNativeContentWidth(payload: unknown): number | null {
  if (typeof payload !== 'number' || !Number.isFinite(payload)) {
    return null;
  }
  const width = Math.ceil(payload);
  if (width <= 0) {
    return null;
  }
  return Math.min(width, RICH_TEXT_NATIVE_MAX_CONTENT_WIDTH);
}

export function nativeRichTextNeedsHScroll(
  contentWidth: number,
  viewportWidth: number,
): boolean {
  return viewportWidth > 0 && contentWidth > viewportWidth + 1;
}
